import { useEffect, useRef, useState } from 'react';
import { ShoppingBag, Camera, Plus, X, Loader2, CheckCircle2, Fuel } from 'lucide-react';
import BottomSheet from './BottomSheet';
import { zmensitObrazek } from '../../lib/obrazek';

/**
 * Nákup přes pokladnu — photo-first, vícestránkový.
 *
 * Obsluha/řidič vyfotí doklad (1..N stran), zvolí platbu (default Karta) a odešle.
 * Doklad jde ke schválení a naváže se na jeho pokladnu (POST /me/smena/nakup).
 * AI sken doplní částku/dodavatele; frontend pollováním čeká na výsledek
 * (GET /me/nakup/{id}/stav). Nejistý sken → ruční částka (PATCH /me/nakup/{id}).
 *
 * Je to jediné místo na focení účtenek. Když sken najde palivo a člověk drží
 * auto, backend z nákupu udělá tankování (TankovaniZDokladu::zalozZNakupu) —
 * sheet to po skenu ukáže. Nejistý sken paliva → fáze 'tankovani' si řekne
 * o litry a cenu za litr (PATCH /me/tankovani/{id}).
 *
 * Fáze: 'foto' → 'processing' → 'done' | 'manual' | 'tankovani'
 */
const PLATBY = [
    { key: 'karta', label: 'Kartou' },
    { key: 'hotovost', label: 'Hotově' },
    { key: 'prevod', label: 'Převodem' },
];

export default function NakupSheet({ open, onClose, onSaved, isDark, isMobile }) {
    const [faze, setFaze] = useState('foto');
    const [strany, setStrany] = useState([]);      // [{ file, preview }]
    const [platba, setPlatba] = useState('karta');
    const [dokladId, setDokladId] = useState(null);
    const [vysledek, setVysledek] = useState(null); // { castka_s_dph, dodavatel, zpusob_platby }
    const [castkaManual, setCastkaManual] = useState('');
    const [litry, setLitry] = useState('');
    const [cenaLitr, setCenaLitr] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const cameraRef = useRef(null);
    const pollRef = useRef(null);

    const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;

    useEffect(() => {
        if (open) {
            setFaze('foto'); setStrany([]); setPlatba('karta'); setDokladId(null);
            setVysledek(null); setCastkaManual(''); setLitry(''); setCenaLitr(''); setError(null); setSubmitting(false);
        }
        return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
    }, [open]);

    // úklid objectURL náhledů
    useEffect(() => () => strany.forEach(s => URL.revokeObjectURL(s.preview)), [strany]);

    const onPick = (files) => {
        const f = files?.[0];
        if (!f) return;
        setError(null);
        setStrany(prev => prev.length >= 8 ? prev : [...prev, { file: f, preview: URL.createObjectURL(f) }]);
    };
    const odeber = (i) => setStrany(prev => prev.filter((_, idx) => idx !== i));

    const odeslat = async () => {
        if (!strany.length) { setError('Nejdřív vyfoť doklad.'); return; }
        setError(null); setSubmitting(true);
        try {
            const fd = new FormData();
            for (const s of strany) {
                const zmenseny = await zmensitObrazek(s.file);
                fd.append('soubor[]', zmenseny);
            }
            fd.append('zpusob_platby', platba);
            const r = await fetch('/api/me/smena/nakup', {
                method: 'POST', headers: { 'X-CSRF-TOKEN': csrf() }, body: fd,
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) { setError(data.error || `Nahrání selhalo (HTTP ${r.status})`); setSubmitting(false); return; }
            setDokladId(data.doklad_id);
            setFaze('processing');
            spustitPolling(data.doklad_id);
        } catch (e) {
            setError('Nahrání selhalo: ' + (e?.message || 'neznámá chyba'));
        } finally {
            setSubmitting(false);
        }
    };

    const spustitPolling = (id) => {
        let pokusy = 0;
        const max = 15; // ~30 s
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(async () => {
            pokusy++;
            try {
                const r = await fetch(`/api/me/nakup/${id}/stav`);
                const d = await r.json().catch(() => ({}));
                const skenDobehl = d.stav && !['nahrany', 'skenuje_se'].includes(d.stav);
                if (r.ok && skenDobehl && d.castka_s_dph > 0) {
                    clearInterval(pollRef.current); pollRef.current = null;
                    setVysledek(d); setFaze('done');
                    // Tankování vzniká až těsně po skenu, takže první odpověď
                    // o něm ještě nemusí vědět — jednou se zeptáme znovu.
                    setTimeout(() => dotahniTankovani(id, d), 1500);
                    return;
                }
                if ((r.ok && skenDobehl && !(d.castka_s_dph > 0)) || pokusy >= max) {
                    clearInterval(pollRef.current); pollRef.current = null;
                    if (d.castka_s_dph > 0) setCastkaManual(String(d.castka_s_dph));
                    setFaze('manual');
                }
            } catch {
                if (pokusy >= max) { clearInterval(pollRef.current); pollRef.current = null; setFaze('manual'); }
            }
        }, 2000);
    };

    const dotahniTankovani = async (id, puvodni) => {
        let d = puvodni;
        if (!d.tankovani) {
            try {
                const r = await fetch(`/api/me/nakup/${id}/stav`);
                if (r.ok) d = await r.json();
            } catch { /* necháme původní */ }
        }
        const t = d.tankovani;
        if (!t) { onSaved?.(); return; }
        setVysledek(d);
        if (t.stav === 'ceka_sken') {
            if (t.litry) setLitry(String(t.litry));
            if (t.cena_kc_litr) setCenaLitr(String(t.cena_kc_litr));
            setFaze('tankovani');
            return;
        }
        // Rozpoznané tankování nechá chvíli na očích — řidič má vidět, že se
        // zapsalo k autu, a ne jen že zmizel sheet.
        setTimeout(() => onSaved?.(), 2500);
    };

    const ulozitTankovani = async () => {
        const l = parseFloat(String(litry).replace(',', '.'));
        const c = parseFloat(String(cenaLitr).replace(',', '.'));
        if (!l || l <= 0 || !c || c <= 0) { setError('Doplň litry i cenu za litr.'); return; }
        setError(null); setSubmitting(true);
        try {
            const r = await fetch(`/api/me/tankovani/${vysledek.tankovani.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf(), Accept: 'application/json' },
                body: JSON.stringify({ litry: l, cena_kc_litr: c }),
            });
            if (!r.ok) { const d = await r.json().catch(() => ({})); setError(d.error || `Uložení selhalo (HTTP ${r.status})`); return; }
            onSaved?.();
        } catch (e) {
            setError('Uložení selhalo: ' + (e?.message || 'neznámá chyba'));
        } finally {
            setSubmitting(false);
        }
    };

    const ulozitCastku = async () => {
        const c = parseFloat(castkaManual);
        if (!c || c <= 0) { setError('Zadej částku dokladu.'); return; }
        setError(null); setSubmitting(true);
        try {
            const r = await fetch(`/api/me/nakup/${dokladId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf(), Accept: 'application/json' },
                body: JSON.stringify({ castka_s_dph: c }),
            });
            if (!r.ok) { const d = await r.json().catch(() => ({})); setError(d.error || `Uložení selhalo (HTTP ${r.status})`); setSubmitting(false); return; }
            onSaved?.();
        } catch (e) {
            setError('Uložení selhalo: ' + (e?.message || 'neznámá chyba'));
        } finally {
            setSubmitting(false);
        }
    };

    const accent = '#0CC0DF';
    const labelMuted = isDark ? 'rgba(255,255,255,0.55)' : 'var(--text-secondary)';
    const textPrimary = isDark ? '#fff' : 'var(--text-primary)';
    const textMuted = isDark ? 'rgba(255,255,255,0.5)' : 'var(--text-muted)';
    const inputBorder = isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)';
    const inputBg = isDark ? 'rgba(0,0,0,0.30)' : 'var(--surface-base)';
    const btnGhost = { flex: 1, padding: '12px', borderRadius: 9, background: 'transparent', color: labelMuted, border: `1px solid ${inputBorder}`, fontSize: 14, fontWeight: 600, cursor: 'pointer' };
    const btnPrimary = (dis) => ({ flex: 2, padding: '12px', borderRadius: 9, background: `linear-gradient(135deg, ${accent} 0%, #0899b3 100%)`, color: '#08202a', border: 'none', fontSize: 14, fontWeight: 800, cursor: dis ? 'wait' : 'pointer', opacity: dis ? 0.5 : 1, boxShadow: `0 6px 20px ${accent}55` });

    return (
        <BottomSheet open={open} onClose={onClose} isDark={isDark} isMobile={isMobile} title={null}>
            <div style={{ padding: '14px 18px 12px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ width: 44, height: 44, borderRadius: 9, background: `${accent}1A`, color: accent, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <ShoppingBag size={22} />
                </span>
                <span style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <span style={{ fontSize: 18, fontWeight: 800, color: textPrimary }}>Účtenka</span>
                    <span style={{ fontSize: 12, color: textMuted }}>Nákup i tankování — tankování poznáme samo.</span>
                </span>
            </div>

            <div style={{ padding: '0 18px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                <input ref={cameraRef} type="file" accept="image/*" capture="environment"
                    onChange={e => { onPick(e.target.files); e.target.value = ''; }} style={{ display: 'none' }} />

                {faze === 'foto' && (
                    <>
                        {/* Náhledy stran */}
                        {strany.length > 0 && (
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {strany.map((s, i) => (
                                    <div key={i} style={{ position: 'relative', width: 84, height: 84 }}>
                                        <img src={s.preview} alt={`Strana ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 7, background: '#000' }} />
                                        <button type="button" onClick={() => odeber(i)} style={{ position: 'absolute', top: -6, right: -6, width: 22, height: 22, borderRadius: 8, background: '#EF4444', color: '#fff', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                                            <X size={13} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        <button type="button" onClick={() => cameraRef.current?.click()}
                            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, padding: strany.length ? '14px' : '30px 16px', borderRadius: 10, cursor: 'pointer', background: `${accent}12`, border: `2px dashed ${accent}66`, color: accent }}>
                            {strany.length ? <Plus size={22} /> : <Camera size={32} />}
                            <span style={{ fontSize: 14, fontWeight: 800 }}>{strany.length ? 'Přidat další stránku' : 'Vyfotit účtenku'}</span>
                            {!strany.length && <span style={{ fontSize: 12, color: textMuted, textAlign: 'center' }}>Vícestránkovou fakturu vyfoť po stranách.</span>}
                        </button>

                        {/* Volba platby */}
                        <div>
                            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: labelMuted }}>Zaplaceno</span>
                            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                                {PLATBY.map(p => {
                                    const on = platba === p.key;
                                    return (
                                        <button key={p.key} type="button" onClick={() => setPlatba(p.key)}
                                            style={{ flex: 1, padding: '10px', borderRadius: 7, fontSize: 13, fontWeight: 700, cursor: 'pointer', background: on ? `${accent}22` : inputBg, color: on ? accent : textMuted, border: `1px solid ${on ? accent + '88' : inputBorder}` }}>
                                            {p.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {error && <ErrorBox error={error} />}

                        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                            <button type="button" onClick={onClose} style={btnGhost}>Zrušit</button>
                            <button type="button" onClick={odeslat} disabled={submitting || !strany.length} style={btnPrimary(submitting || !strany.length)}>
                                {submitting ? 'Odesílám…' : 'Odeslat doklad'}
                            </button>
                        </div>
                    </>
                )}

                {faze === 'processing' && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '28px 16px' }}>
                        <Loader2 size={34} className="animate-spin" style={{ color: accent }} />
                        <span style={{ fontSize: 15, fontWeight: 700, color: textPrimary }}>Čtu doklad…</span>
                        <span style={{ fontSize: 12, color: textMuted, textAlign: 'center' }}>Doklad je odeslaný ke schválení. Za chvíli doplníme částku.</span>
                        <button type="button" onClick={onClose} style={{ ...btnGhost, flex: 'none', marginTop: 4 }}>Zavřít (dokončí se na pozadí)</button>
                    </div>
                )}

                {faze === 'done' && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '28px 16px' }}>
                        {vysledek?.tankovani
                            ? <Fuel size={40} style={{ color: '#3B82F6' }} />
                            : <CheckCircle2 size={40} style={{ color: '#22C55E' }} />}
                        <span style={{ fontSize: 16, fontWeight: 800, color: textPrimary }}>
                            {vysledek?.tankovani ? 'Tankování zapsáno' : 'Doklad odeslán ✓'}
                        </span>
                        {vysledek?.tankovani && (
                            <span style={{ fontSize: 14, fontWeight: 700, color: '#3B82F6', fontVariantNumeric: 'tabular-nums' }}>
                                {vysledek.tankovani.litry ? `${String(vysledek.tankovani.litry).replace('.', ',')} l` : ''}
                                {vysledek.tankovani.spz ? ` · ${vysledek.tankovani.spz}` : ''}
                            </span>
                        )}
                        <span style={{ fontSize: 15, color: textMuted, fontVariantNumeric: 'tabular-nums' }}>
                            {vysledek?.castka_s_dph != null && <>{Math.round(Number(vysledek.castka_s_dph)).toLocaleString('cs-CZ')} Kč</>}
                            {vysledek?.dodavatel && <> · {vysledek.dodavatel}</>}
                        </span>
                        <span style={{ fontSize: 12, color: textMuted }}>{PLATBY.find(p => p.key === vysledek?.zpusob_platby)?.label}</span>
                    </div>
                )}

                {faze === 'tankovani' && (
                    <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 9, background: 'rgba(59,130,246,0.10)', border: '1px solid rgba(59,130,246,0.30)' }}>
                            <Fuel size={18} style={{ color: '#3B82F6', flexShrink: 0 }} />
                            <span style={{ fontSize: 13, color: textPrimary, lineHeight: 1.4 }}>
                                Na účtence je tankování{vysledek?.tankovani?.spz ? ` do ${vysledek.tankovani.spz}` : ''}. Litry se nepodařilo přečíst jistě — zkontroluj je prosím.
                            </span>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: labelMuted }}>Litry *</span>
                                <input type="number" inputMode="decimal" step="0.01" min="0.01" value={litry}
                                    onChange={e => setLitry(e.target.value)} placeholder="0,00"
                                    style={{ width: '100%', padding: '10px 12px', borderRadius: 7, fontSize: 14, fontWeight: 700, background: inputBg, color: textPrimary, border: `1px solid ${inputBorder}`, boxSizing: 'border-box', outline: 'none', fontVariantNumeric: 'tabular-nums' }} />
                            </label>
                            <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: labelMuted }}>Kč za litr *</span>
                                <input type="number" inputMode="decimal" step="0.01" min="0.01" value={cenaLitr}
                                    onChange={e => setCenaLitr(e.target.value)} placeholder="0,00"
                                    style={{ width: '100%', padding: '10px 12px', borderRadius: 7, fontSize: 14, fontWeight: 700, background: inputBg, color: textPrimary, border: `1px solid ${inputBorder}`, boxSizing: 'border-box', outline: 'none', fontVariantNumeric: 'tabular-nums' }} />
                            </label>
                        </div>
                        {error && <ErrorBox error={error} />}
                        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                            <button type="button" onClick={onClose} style={btnGhost}>Později</button>
                            <button type="button" onClick={ulozitTankovani} disabled={submitting} style={btnPrimary(submitting)}>
                                {submitting ? 'Ukládám…' : 'Uložit tankování'}
                            </button>
                        </div>
                    </>
                )}

                {faze === 'manual' && (
                    <>
                        <div style={{ padding: '8px 12px', borderRadius: 7, fontSize: 12, background: 'rgba(245,158,11,0.12)', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.3)' }}>
                            Doklad se nepodařilo přečíst automaticky — doplň prosím celkovou částku.
                        </div>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: labelMuted }}>Celkem Kč *</span>
                            <input type="number" inputMode="decimal" step="0.01" min="0.01" value={castkaManual}
                                onChange={e => setCastkaManual(e.target.value)} placeholder="0,00"
                                style={{ width: '100%', padding: '10px 12px', borderRadius: 7, fontSize: 14, fontWeight: 700, background: inputBg, color: textPrimary, border: `1px solid ${inputBorder}`, boxSizing: 'border-box', outline: 'none', fontVariantNumeric: 'tabular-nums' }} />
                        </label>
                        {error && <ErrorBox error={error} />}
                        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                            <button type="button" onClick={onClose} style={btnGhost}>Zavřít</button>
                            <button type="button" onClick={ulozitCastku} disabled={submitting} style={btnPrimary(submitting)}>
                                {submitting ? 'Ukládám…' : 'Uložit'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </BottomSheet>
    );
}

function ErrorBox({ error }) {
    return (
        <div style={{ padding: '8px 12px', borderRadius: 7, fontSize: 12, whiteSpace: 'pre-line', background: 'rgba(239,68,68,0.12)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)' }}>{error}</div>
    );
}
